import IconExport from "assets/img/icon-export.svg?react"
import IconPlus from "assets/img/icon-plus.svg?react"

import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import AssetInspection, { AssetInspectionType } from "models/AssetInspection"
import AssetInspectionPair from "models/AssetInspectionPair"
import { Grid, GridItem } from "components/Grid"
import Text from "components/Text/Text"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import { IGridProps } from "components/Grid/Grid"
import ImageCollectionModel from "models/ImageCollection"
import ImageCollection from "components/ImageCollection/ImageCollection"
import PictureGalleryModal from "components/PictureGalleryModal/PictureGalleryModal"
import { IPictureGalleryItem } from "components/PictureGallery/PictureGallery"
import { Flex } from "components/Flex"
import Button from "components/Button/Button"
import CollapsibleSection from "components/CollapsibleSection"
import AssetInspectionModal from "./AssetInspectionModal"
import { Guid } from "@/types"
import { formatNumber, getFuelLabel } from "utils/utils"

interface IAssetInspectionsProps {
    assetId?: Guid
    inspectionPairs: AssetInspectionPair[] | null
    latestCondition?: number
    latestFuel?: number
    latestOperationHours?: number
    latestMileage?: number
    latestAdBlue?: number
    latestRefuel?: number
    latestLsva?: number
    isMachine?: boolean
    onInspectionCreated?: () => void
    onInspectionExportClick?: (deliveryInspectionId?: Guid, returnInspectionId?: Guid) => Promise<void>
}

const AssetInspections: FC<IAssetInspectionsProps> = ({
    assetId,
    inspectionPairs,
    latestCondition,
    latestFuel,
    latestOperationHours,
    latestMileage,
    latestAdBlue,
    latestRefuel,
    latestLsva,
    isMachine,
    onInspectionCreated,
    onInspectionExportClick,
}) => {
    const { t } = useTranslation()
    const [pictureGalleryItems, setPictureGalleryItems] = useState<IPictureGalleryItem[] | null>(null)
    const [pictureGalleryActiveIndex, setPictureGalleryActiveIndex] = useState<number | null>(null)
    const [createAssetInspectionType, setCreateAssetInspectionType] = useState<AssetInspectionType | null>(null)
    const [createInspectionShippingOrderId, setCreateInspectionShippingOrderId] = useState<Guid | null>(null)
    const [editingInspection, setEditingInspection] = useState<AssetInspection | null>(null)
    const [isExportingInspection, setIsExportingInspection] = useState(false)

    const gridProps: IGridProps = {
        columns: "minmax(0, 208fr) minmax(0, 108fr) minmax(0, 108fr)",
        gap: "1.2rem",
    }

    function renderInspectionRow({
        label,
        deliveryValue,
        returnValue,
        returnSubvalue,
    }: {
        label: string
        deliveryValue?: string
        returnValue?: string
        returnSubvalue?: string
    }) {
        return (
            <Grid {...gridProps} className="py-1-5 border-top-grey-100">
                <GridItem>
                    {label}
                </GridItem>
                <GridItem className="font-bold text-right">
                    {deliveryValue ? <FlexibleContent text={deliveryValue} /> : "–"}
                </GridItem>
                <GridItem className="font-bold text-right">
                    {returnValue ? <FlexibleContent text={returnValue} /> : "–"}
                    {!!returnSubvalue &&
                        <Text type="pSmallest" className="color-grey-500">{returnSubvalue}</Text>
                    }
                </GridItem>
            </Grid>
        )
    }

    function renderInspectionDescription({
        title,
        comment,
        images,
    }: {
        title: string
        comment?: string
        images: ImageCollectionModel
    }) {
        return (
            <div className="mt-3 pt-3 border-top-grey-100">
                <Text type="h4" className="mb-2">{title}</Text>
                {!!comment &&
                    <Text preLine className="mb-2">{comment}</Text>
                }
                <ImageCollection
                    data={images}
                    onImageClick={imageIndex => handleImageCollectionImageClick(images, imageIndex)}
                />
            </div>
        )
    }

    function handleImageCollectionImageClick(collection: ImageCollectionModel, imageIndex: number) {
        const newPictureGalleryItems = collection.images?.map((image, index) => ({
            index,
            image,
            date: collection.collectionDate,
            tags: collection.tags,
        }))
        setPictureGalleryActiveIndex(imageIndex)
        setPictureGalleryItems(newPictureGalleryItems || null)
    }

    function handleAssetInspectionModalClose() {
        setCreateAssetInspectionType(null)
        setCreateInspectionShippingOrderId(null)
        setEditingInspection(null)
    }

    function handleCreateInspection(type: AssetInspectionType, shippingOrderId: Guid) {
        setCreateAssetInspectionType(type)
        setCreateInspectionShippingOrderId(shippingOrderId)
    }

    const handleInspectionExportClick = useCallback(async (deliveryInspectionId?: Guid, returnInspectionId?: Guid) => {
        if (!onInspectionExportClick) return
        setIsExportingInspection(true)
        try {
            await onInspectionExportClick(deliveryInspectionId, returnInspectionId)
        } finally {
            setIsExportingInspection(false)
        }
    }, [onInspectionExportClick])

    function renderInspectionPair(pair: AssetInspectionPair, index: number) {
        const deliveryInspection = pair.deliveryInspection
        const returnInspection = pair.returnInspection

        // Determine the last (most recent) inspection for the title
        const lastInspection = deliveryInspection && returnInspection
            ? (returnInspection.inspectionDate.isAfter(deliveryInspection.inspectionDate) ? returnInspection : deliveryInspection)
            : (deliveryInspection || returnInspection)

        // Create title with last inspection date & user email, or fallback
        const pairTitle = lastInspection
            ? `${lastInspection.inspectionDate.format(t('config.dateLongTimeFormat'))}, ${lastInspection.inspectedByUserEmail}`
            : `${t('assetInspection.inspection')} ${index + 1}`

        // Calculate operating hours diff for this pair
        let operatingHoursDiff = 0
        if (deliveryInspection && returnInspection
            && typeof deliveryInspection.operationHours === "number"
            && typeof returnInspection.operationHours === "number") {
            operatingHoursDiff = returnInspection.operationHours - deliveryInspection.operationHours
        }

        return (
            <CollapsibleSection
                key={`inspection-pair-${index}`}
                title={pairTitle}
                className="mb-1"
                defaultExpanded={index === 0}
            >
                <div className="px-1 py-1">
                    {(!!deliveryInspection || !!returnInspection) && (
                        <>
                            <Grid {...gridProps} className="color-grey-500 text-right mb-half">
                                <GridItem className="text-left">
                                    {!!(deliveryInspection || returnInspection) && !!onInspectionExportClick && (
                                        <Button
                                            theme="link"
                                            label={t('assetInspection.exportInspection')}
                                            icon={<IconExport />}
                                            iconPosition="end"
                                            size="small"
                                            loading={isExportingInspection}
                                            disabled={isExportingInspection}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleInspectionExportClick(deliveryInspection?.id, returnInspection?.id)
                                            }}
                                        />
                                    )}
                                </GridItem>
                                <Text type="pSmallest">
                                    <div className="font-bold mb-half">{t('assetInspection.deliveryInspection')}</div>
                                    {!!deliveryInspection && (
                                        <>
                                            <div>{deliveryInspection.inspectionDate.format(t('config.dateLongTimeFormat'))},</div>
                                            <FlexibleContent text={deliveryInspection.inspectedByUserEmail} />
                                            {!!(deliveryInspection.linkUpdateInspection || deliveryInspection.linkUpdateInspectionComment) && (
                                                <Button
                                                    theme="link"
                                                    label={t('common.edit')}
                                                    className="text-right"
                                                    onClick={() => setEditingInspection(deliveryInspection)}
                                                />
                                            )}
                                        </>
                                    )}
                                </Text>
                                <Text type="pSmallest">
                                    <div className="font-bold mb-half">{t('assetInspection.returnInspection')}</div>
                                    {!!returnInspection && (
                                        <>
                                            <div>{returnInspection.inspectionDate.format(t('config.dateLongTimeFormat'))},</div>
                                            <FlexibleContent text={returnInspection.inspectedByUserEmail} />
                                            {!!(returnInspection.linkUpdateInspection || returnInspection.linkUpdateInspectionComment) && (
                                                <Button
                                                    theme="link"
                                                    label={t('common.edit')}
                                                    className="text-right"
                                                    onClick={() => setEditingInspection(returnInspection)}
                                                />
                                            )}
                                        </>
                                    )}
                                </Text>
                            </Grid>
                            {renderInspectionRow({
                                label: t('assetInspection.damages'),
                                deliveryValue: deliveryInspection ? (deliveryInspection.damages ? t('common.yes') : t('common.no')) : undefined,
                                returnValue: returnInspection ? (returnInspection.damages ? t('common.yes') : t('common.no')) : undefined,
                            })}
                            {renderInspectionRow({
                                label: t('asset.operationalState'),
                                deliveryValue: deliveryInspection ? (deliveryInspection.isOperational ? t('assetInspection.operational') : t('assetInspection.notOperational')) : undefined,
                                returnValue: returnInspection ? (returnInspection.isOperational ? t('assetInspection.operational') : t('assetInspection.notOperational')) : undefined,
                            })}
                            {renderInspectionRow({
                                label: t('asset.condition'),
                                deliveryValue: deliveryInspection ? `${deliveryInspection.condition} %` : undefined,
                                returnValue: returnInspection ? `${returnInspection.condition} %` : undefined,
                            })}
                            {renderInspectionRow({
                                label: t('assetInspection.fuel'),
                                deliveryValue: typeof deliveryInspection?.fuel === "number" ? getFuelLabel(deliveryInspection.fuel) : undefined,
                                returnValue: typeof returnInspection?.fuel === "number" ? getFuelLabel(returnInspection.fuel) : undefined,
                            })}
                            {renderInspectionRow({
                                label: t('asset.operatingHours'),
                                deliveryValue: typeof deliveryInspection?.operationHours === "number" ? t('common.xHours', { count: deliveryInspection.operationHours }) : undefined,
                                returnValue: typeof returnInspection?.operationHours === "number" ? t('common.xHours', { count: returnInspection.operationHours }) : undefined,
                                returnSubvalue: operatingHoursDiff !== 0 ? `${operatingHoursDiff > 0 ? '+' : '-'} ${t('common.xHours', { count: Math.abs(operatingHoursDiff) })}` : undefined,
                            })}
                            {renderInspectionRow({
                                label: t('assetInspection.mileage'),
                                deliveryValue: typeof deliveryInspection?.mileage === "number" ? `${formatNumber(deliveryInspection.mileage)} ${t('common.km')}` : undefined,
                                returnValue: typeof returnInspection?.mileage === "number" ? `${formatNumber(returnInspection.mileage)} ${t('common.km')}` : undefined,
                            })}
                            {renderInspectionRow({
                                label: t('assetInspection.adBlue'),
                                deliveryValue: typeof deliveryInspection?.adBlue === "number" ? getFuelLabel(deliveryInspection.adBlue) : undefined,
                                returnValue: typeof returnInspection?.adBlue === "number" ? getFuelLabel(returnInspection.adBlue) : undefined,
                            })}
                            {renderInspectionRow({
                                label: t('assetInspection.refuel'),
                                deliveryValue: undefined, // Refuel is only shown for return inspection
                                returnValue: typeof returnInspection?.refuel === "number" ? formatNumber(returnInspection.refuel) + ` ${t('assetInspection.refuelLiters')}` : undefined,
                            })}
                            {renderInspectionRow({
                                label: t('assetInspection.lsva'),
                                deliveryValue: typeof deliveryInspection?.lsva === "number" ? formatNumber(deliveryInspection.lsva, 2) : undefined,
                                returnValue: typeof returnInspection?.lsva === "number" ? formatNumber(returnInspection.lsva, 2) : undefined,
                            })}
                            {!!deliveryInspection && renderInspectionDescription({
                                title: t('assetInspection.deliveryInspection'),
                                comment: deliveryInspection.comment ?? undefined,
                                images: deliveryInspection.images,
                            })}
                            {!!returnInspection && renderInspectionDescription({
                                title: t('assetInspection.returnInspection'),
                                comment: returnInspection.comment ?? undefined,
                                images: returnInspection.images,
                            })}
                        </>
                    )}
                </div>
            </CollapsibleSection>
        )
    }

    // If no inspection pairs, don't render anything
    if (!inspectionPairs || inspectionPairs.length === 0) {
        return null
    }

    // Filter to only show pairs that have at least one actual inspection
    // Hide completely empty pairs (no inspections at all)
    const validInspectionPairs = inspectionPairs.filter(pair => {
        const hasDeliveryInspection = !!pair.deliveryInspection
        const hasReturnInspection = !!pair.returnInspection

        // Only show pairs that have at least one inspection
        return hasDeliveryInspection || hasReturnInspection
    })

    // Collect all required inspections from all pairs for the top-level buttons
    const allRequiredInspections = inspectionPairs.reduce((acc, pair) => {
        const needsDeliveryInspection = pair.deliveryInspectionRequired &&
            !pair.deliveryInspection &&
            (pair.deliveryInspectionId === "00000000-0000-0000-0000-000000000000" || pair.deliveryInspectionId === null) &&
            pair.deliveryShippingOrderId

        const needsReturnInspection = pair.returnInspectionRequired &&
            !pair.returnInspection &&
            (pair.returnInspectionId === "00000000-0000-0000-0000-000000000000" || pair.returnInspectionId === null) &&
            pair.returnShippingOrderId

        if (needsDeliveryInspection) {
            acc.push({
                type: AssetInspectionType.delivery,
                shippingOrderId: pair.deliveryShippingOrderId!,
                label: t('assetInspection.performDeliveryInspection')
            })
        }

        if (needsReturnInspection) {
            acc.push({
                type: AssetInspectionType.return,
                shippingOrderId: pair.returnShippingOrderId!,
                label: t('assetInspection.performReturnInspection')
            })
        }

        return acc
    }, [] as Array<{ type: AssetInspectionType, shippingOrderId: Guid, label: string }>)

    // If no valid inspection pairs and no required inspections, don't render anything
    if (validInspectionPairs.length === 0 && allRequiredInspections.length === 0) {
        return null
    }

    return (
        <>
            {/* Show required inspection creation buttons at the top */}
            {allRequiredInspections.length > 0 && (
                <div className="round-sm-top border-left-grey-100 border-right-grey-100 border-top-grey-100 p-3">
                    <Flex flow="column" gap="1.6rem">
                        {allRequiredInspections.map((requiredInspection, index) => (
                            <Button
                                key={`${requiredInspection.type}-${requiredInspection.shippingOrderId}-${index}`}
                                label={requiredInspection.label}
                                icon={<IconPlus />}
                                onClick={() => handleCreateInspection(requiredInspection.type, requiredInspection.shippingOrderId)}
                                className="w-100"
                            />
                        ))}
                    </Flex>
                </div>
            )}

            {/* Show existing inspections */}
            {validInspectionPairs.length > 0 && (
                <div className={`${allRequiredInspections.length > 0 ? 'round-sm-bottom' : 'round-sm'} border-left-grey-100 border-right-grey-100 border-bottom-grey-100 ${allRequiredInspections.length > 0 ? '' : 'border-top-grey-100'}`}>
                    {validInspectionPairs.map((pair, index) => renderInspectionPair(pair, index))}
                </div>
            )}

            {!!pictureGalleryItems && (
                <PictureGalleryModal
                    pictureGallery={{
                        items: pictureGalleryItems,
                        activeIndex: pictureGalleryActiveIndex || 0,
                    }}
                    onClose={() => setPictureGalleryItems(null)}
                />
            )}

            {!!assetId && !!createAssetInspectionType && !!createInspectionShippingOrderId && (
                <AssetInspectionModal
                    assetId={assetId}
                    shippingOrderId={createInspectionShippingOrderId}
                    inspectionType={createAssetInspectionType}
                    latestCondition={latestCondition}
                    latestFuel={latestFuel}
                    latestOperationHours={latestOperationHours}
                    latestMileage={latestMileage}
                    latestAdBlue={latestAdBlue}
                    latestRefuel={latestRefuel}
                    latestLsva={latestLsva}
                    inspection={null}
                    isMachine={isMachine}
                    onClose={handleAssetInspectionModalClose}
                    onInspectionCreatedOrUpdated={onInspectionCreated}
                />
            )}

            {!!assetId && !!editingInspection && (
                <AssetInspectionModal
                    assetId={assetId}
                    shippingOrderId={editingInspection.shippingOrderId}
                    inspectionType={editingInspection.inspectionType}
                    latestCondition={undefined}
                    latestFuel={undefined}
                    latestOperationHours={latestOperationHours}
                    latestMileage={latestMileage}
                    latestAdBlue={undefined}
                    latestRefuel={undefined}
                    latestLsva={undefined}
                    inspection={editingInspection}
                    isMachine={isMachine}
                    onClose={handleAssetInspectionModalClose}
                    onInspectionCreatedOrUpdated={onInspectionCreated}
                />
            )}
        </>
    )
}

export default AssetInspections
